import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building2, Save, Plus, Trash2, Pencil, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const empty = { business_name: "", address: "", phone: "", email: "", gst_number: "",
  logo: "", invoice_prefix: "INV", invoice_footer: "", branches: [] };

export default function Organisation() {
  const { user } = useAuth();
  const isOwner = user.role === "owner" || (user.is_platform_admin && (user.platform_role || "super_admin") === "super_admin");
  const [orgs, setOrgs] = useState([]);
  const [view, setView] = useState("list"); // list | form
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(empty);
  const [newBranch, setNewBranch] = useState({ name: "", address: "", phone: "" });
  const [delConfirm, setDelConfirm] = useState(null);

  const load = () => api.get("/organisation").then(r => setOrgs(Array.isArray(r.data) ? r.data : []))
    .catch(e => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setF(empty); setEditId(null); setView("form"); };
  const openEdit = (o) => { setF({ ...empty, ...o, branches: o.branches || [] }); setEditId(o.id); setView("form"); };

  const save = async () => {
    try {
      const payload = { ...f,
        address: f.address || null, phone: f.phone || null,
        email: f.email || null, gst_number: f.gst_number || null,
        logo: f.logo || null, invoice_footer: f.invoice_footer || null,
        branches: (f.branches || []).map(b => ({ name: b.name, address: b.address || null, phone: b.phone || null })) };
      if (editId) await api.put(`/organisation/${editId}`, payload);
      else await api.post("/organisation", payload);
      toast.success(editId ? "Organisation updated" : "Organisation created");
      setView("list"); setEditId(null); setF(empty); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/organisation/${delConfirm.id}`);
      toast.success(`"${delConfirm.business_name}" deleted`);
      setDelConfirm(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const onLogo = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const rd = new FileReader();
    rd.onload = () => setF(x => ({ ...x, logo: rd.result }));
    rd.readAsDataURL(file);
  };

  const addBranchLocal = () => {
    if (!newBranch.name) { toast.error("Branch name required"); return; }
    setF(x => ({ ...x, branches: [...(x.branches || []), { id: `new-${Date.now()}`, ...newBranch }] }));
    setNewBranch({ name: "", address: "", phone: "" });
  };
  const delBranchLocal = (id) => {
    setF(x => ({ ...x, branches: (x.branches || []).filter(b => b.id !== id) }));
  };

  // ---------- LIST VIEW ----------
  if (view === "list") return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Building2 className="w-3 h-3"/> Owner only · Business identity</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Organisation Master</h1>
        </div>
        {isOwner && (
          <Button data-testid="new-org-btn" onClick={openNew} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs">
            <Plus className="w-4 h-4 mr-1"/> New Organisation
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {orgs.length === 0 && (
          <div className="col-span-2 border border-border bg-card p-10 rounded-sm text-center text-muted-foreground">
            No organisation yet — click <span className="text-primary font-semibold">New Organisation</span> to set up your business.
          </div>
        )}
        {orgs.map(o => (
          <div key={o.id} data-testid={`org-card-${o.id}`} className="border border-border bg-card rounded-sm p-4 flex gap-4">
            {o.logo
              ? <img src={o.logo} alt="" className="w-14 h-14 object-contain border border-border rounded-sm bg-secondary p-1"/>
              : <div className="w-14 h-14 border border-border rounded-sm bg-secondary flex items-center justify-center"><Building2 className="w-6 h-6 text-muted-foreground"/></div>}
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{o.business_name}</div>
              <div className="text-xs text-muted-foreground font-mono">{o.gst_number || "No GST"} · {o.invoice_prefix || "INV"}</div>
              <div className="text-xs text-muted-foreground mt-1">{o.phone || "—"} · {(o.branches || []).length} branch(es)</div>
            </div>
            {isOwner && (
              <div className="flex flex-col gap-1">
                <Button data-testid={`edit-org-${o.id}`} size="sm" variant="ghost" onClick={() => openEdit(o)} className="h-7 text-xs"><Pencil className="w-3 h-3 mr-1"/> Edit</Button>
                <Button data-testid={`del-org-${o.id}`} size="sm" variant="ghost" onClick={() => setDelConfirm(o)} className="h-7 text-xs text-destructive"><Trash2 className="w-3 h-3 mr-1"/> Delete</Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {delConfirm && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50" data-testid="delete-org-dialog">
          <div className="border border-destructive/40 bg-card rounded-sm p-6 max-w-sm w-full">
            <div className="font-display font-black text-lg uppercase mb-2 text-destructive">Delete Organisation?</div>
            <div className="text-sm text-muted-foreground mb-4">
              This will permanently remove <span className="text-foreground font-semibold">"{delConfirm.business_name}"</span> and its {(delConfirm.branches || []).length} branch(es). This cannot be undone.
            </div>
            <div className="flex gap-2">
              <Button data-testid="confirm-del-org" onClick={confirmDelete} className="flex-1 rounded-sm bg-destructive text-destructive-foreground uppercase tracking-wider text-xs">Yes, Delete</Button>
              <Button data-testid="cancel-del-org" onClick={() => setDelConfirm(null)} variant="outline" className="flex-1 rounded-sm uppercase tracking-wider text-xs">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ---------- FORM VIEW (New / Edit) ----------
  return (
    <div className="p-8">
      <button onClick={() => { setView("list"); setF(empty); setEditId(null); }} className="text-xs uppercase tracking-wider text-muted-foreground mb-2 hover:text-primary flex items-center gap-1">
        <ArrowLeft className="w-3 h-3"/> Back to Organisations
      </button>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2">{editId ? "Edit" : "New"} Organisation</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">{editId ? f.business_name || "Edit" : "Create"} Organisation</h1>
        </div>
        <Button data-testid="save-org-btn" onClick={save} disabled={!f.business_name} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs"><Save className="w-4 h-4 mr-1"/> {editId ? "Update" : "Create"}</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-border bg-card rounded-sm p-5 space-y-3">
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Business Name</Label><Input data-testid="org-name" value={f.business_name} onChange={(e) => setF(x => ({ ...x, business_name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Phone</Label><Input data-testid="org-phone" value={f.phone || ""} onChange={(e) => setF(x => ({ ...x, phone: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</Label><Input data-testid="org-email" value={f.email || ""} onChange={(e) => setF(x => ({ ...x, email: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
          </div>
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Address</Label><Textarea data-testid="org-address" value={f.address || ""} onChange={(e) => setF(x => ({ ...x, address: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={2}/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">GST Number</Label><Input data-testid="org-gst" value={f.gst_number || ""} onChange={(e) => setF(x => ({ ...x, gst_number: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border font-mono"/></div>
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Invoice Prefix</Label><Input data-testid="org-prefix" value={f.invoice_prefix || ""} onChange={(e) => setF(x => ({ ...x, invoice_prefix: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border font-mono"/></div>
          </div>
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Invoice Footer</Label><Textarea data-testid="org-footer" value={f.invoice_footer || ""} onChange={(e) => setF(x => ({ ...x, invoice_footer: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={2} placeholder="Thanks for your business · Terms & conditions apply"/></div>
        </div>
        <div className="border border-border bg-card rounded-sm p-5 space-y-3">
          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Logo</Label>
          {f.logo && <img src={f.logo} alt="logo" className="max-h-32 border border-border rounded-sm bg-secondary p-2 object-contain"/>}
          <Input data-testid="org-logo" type="file" accept="image/*" onChange={onLogo} className="rounded-sm bg-secondary border-border"/>
          {f.logo && <Button size="sm" variant="ghost" onClick={() => setF(x => ({ ...x, logo: "" }))} className="text-xs text-destructive"><Trash2 className="w-3 h-3 mr-1"/> Remove logo</Button>}
        </div>
      </div>

      <div className="mt-6 border border-border bg-card rounded-sm p-5">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Branches ({(f.branches || []).length})</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
          <Input data-testid="br-name" placeholder="Branch name" value={newBranch.name} onChange={(e) => setNewBranch(x => ({ ...x, name: e.target.value }))} className="rounded-sm bg-secondary border-border"/>
          <Input data-testid="br-address" placeholder="Address" value={newBranch.address} onChange={(e) => setNewBranch(x => ({ ...x, address: e.target.value }))} className="rounded-sm bg-secondary border-border"/>
          <Input data-testid="br-phone" placeholder="Phone" value={newBranch.phone} onChange={(e) => setNewBranch(x => ({ ...x, phone: e.target.value }))} className="rounded-sm bg-secondary border-border"/>
          <Button data-testid="add-br-btn" onClick={addBranchLocal} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-wider text-xs"><Plus className="w-3 h-3 mr-1"/> Add Branch</Button>
        </div>
        <div className="space-y-2">
          {(f.branches || []).length === 0 && <div className="text-xs text-muted-foreground italic">No branches — single-location setup.</div>}
          {(f.branches || []).map(b => (
            <div key={b.id} data-testid={`br-${b.id}`} className="flex items-center justify-between border border-border rounded-sm px-3 py-2 text-sm">
              <div>
                <div className="font-semibold">{b.name}</div>
                <div className="text-xs text-muted-foreground">{b.address || "—"} · <span className="font-mono">{b.phone || "—"}</span></div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => delBranchLocal(b.id)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
